import { useCallback, useState, useEffect } from 'react';
import type { Task } from '../types/project';
import type { Person } from '../types/people';
import AvatarStack from './AvatarStack';

interface TaskAssigneesPickerProps {
  task: Task;
  people: Person[];
  onChange: (personIds: string[]) => void;
}

export default function TaskAssigneesPicker({ task, people, onChange }: TaskAssigneesPickerProps) {
  const [open, setOpen] = useState(false);
  const [pendingIds, setPendingIds] = useState<string[]>(() =>
    task.assignee_ids && task.assignee_ids.length > 0
      ? task.assignee_ids
      : task.assignee
      ? [task.assignee]
      : []
  );

  useEffect(() => {
    setPendingIds(
      task.assignee_ids && task.assignee_ids.length > 0
        ? task.assignee_ids
        : task.assignee
        ? [task.assignee]
        : []
    );
  }, [task.assignee, task.assignee_ids]);

  const togglePerson = useCallback((id: string) => {
    setPendingIds((prev) => (prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]));
  }, []);

  const handleSave = useCallback(() => {
    onChange(pendingIds);
    setOpen(false);
  }, [onChange, pendingIds]);

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: '2px 4px',
          borderRadius: '999px',
          border: '1px solid var(--color-border)',
          background: 'white',
          cursor: 'pointer',
        }}
      >
        <AvatarStack people={people} personIds={pendingIds} size={20} />
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>▼</span>
      </button>
      {open && (
        <>
          <div
            role="presentation"
            style={{ position: 'fixed', inset: 0, zIndex: 30 }}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          />
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              marginTop: 4,
              zIndex: 31,
              minWidth: 200,
              maxHeight: 260,
              overflowY: 'auto',
              padding: '10px 12px',
              background: 'white',
              borderRadius: 'var(--radius)',
              border: '1px solid var(--color-border)',
              boxShadow: '0 4px 12px rgba(15,23,42,0.16)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                marginBottom: 8,
              }}
            >
              Assign people
            </div>
            {people.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 8 }}>
                No people
              </div>
            ) : (
              people.map((person) => {
                const checked = pendingIds.includes(person.id);
                return (
                  <div
                    key={person.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => togglePerson(person.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        togglePerson(person.id);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 4px',
                      cursor: 'pointer',
                      fontSize: 13,
                      borderRadius: 'var(--radius)',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      readOnly
                      tabIndex={-1}
                      style={{ pointerEvents: 'none', flexShrink: 0 }}
                    />
                    <span>{person.name}</span>
                  </div>
                );
              })
            )}
            <button
              type="button"
              onClick={handleSave}
              style={{
                marginTop: 8,
                width: '100%',
                padding: '6px 10px',
                fontSize: 13,
                fontWeight: 500,
                background: 'var(--color-blue-dark)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
              }}
            >
              Save
            </button>
          </div>
        </>
      )}
    </div>
  );
}

